/**
 * annotationModel — 在截圖上圈出問題的位置(R250 建、R257 簡化)
 * =============================================================================
 *
 * ⚠️ **R257 移除了「反查元件」那一半**。原本圈選會用 `elementsFromPoint` 找出座標底下最近的
 * `data-testid`,讓回報單直接對到元件檔案。那個加值**只對「我們自己拍的那一屏」成立** ——
 * 而自動截圖已於同輪移除(原理上太慢,見 [screenshotCapture](./screenshotCapture.ts) 檔頭)。
 *
 * 對使用者用系統截圖工具拍的圖硬做反查,會給出**看起來合理但是錯的** testid:那份圖可能是
 * 整個螢幕、可能裁過、可能是別台機器拍的,與當下 DOM 沒有已知的座標關係。錯的 testid 會把
 * 後端指向錯誤的元件檔案,比沒有更糟。
 *
 * 所以現在標註只有幾何:框在哪裡。框會被燒進圖片(人一眼看到),座標也照送(給程式用)。
 */

/** 截圖像素座標系下的矩形。`[x, y, w, h]`,一律非負整數。 */
export type AnnotationRect = readonly [number, number, number, number];

export interface Annotation {
  rect: AnnotationRect;
}

/** 一個框至少要這麼大才算「圈」而不是誤觸(截圖像素)。 */
export const MIN_RECT_EDGE = 8;

/**
 * 兩個拖曳端點 → 正規化矩形。
 *
 * 反向拖(從右下往左上)也要得到正的寬高 —— 使用者不會、也不該注意自己的拖曳方向。
 */
export function rectFromDrag(
  start: { x: number; y: number },
  end: { x: number; y: number },
): AnnotationRect {
  const x = Math.round(Math.min(start.x, end.x));
  const y = Math.round(Math.min(start.y, end.y));
  const w = Math.round(Math.abs(end.x - start.x));
  const h = Math.round(Math.abs(end.y - start.y));
  return [x, y, w, h];
}

export function isMeaningfulRect(rect: AnnotationRect): boolean {
  return rect[2] >= MIN_RECT_EDGE && rect[3] >= MIN_RECT_EDGE;
}

/** 標註 → 一行給人 / AI 讀的字串(進診斷碼的可讀段)。 */
export function formatAnnotation(a: Annotation): string {
  const [x, y, w, h] = a.rect;
  return `rect(${x},${y},${w},${h})`;
}
